import { frameWidget } from "@/elements/frame";
import type { Group } from "@/group";

/**
 * Централизованный менеджер для управления overlapped группами всех фреймов.
 * Гарантирует, что каждая группа может быть захвачена только одним фреймом.
 * Также отслеживает удаляемые группы для предотвращения повторного удаления.
 */
class FrameOverlappedManager {
    // Map: frameUID -> массив захваченных групп
    private frameToGroups: Map<string, Group[]> = new Map();

    // Set для быстрой проверки уникальности
    private capturedUIDs: Set<string> = new Set();

    // Set для отслеживания групп, помеченных на удаление
    private deletionUIDs: Set<string> = new Set();

    /**
     * Очищает данные об overlapped группах
     */
    clearOverlapped() {
        this.frameToGroups.clear();
        this.capturedUIDs.clear();
    }

    /**
     * Помечает группу как удаляемую
     * @param groupUID - UID группы
     * @returns true если группа помечена впервые, false если уже была помечена
     */
    markForDeletion(groupUID: string): boolean {
        if (this.deletionUIDs.has(groupUID)) {
            return false;
        }
        this.deletionUIDs.add(groupUID);
        return true;
    }

    /**
     * Пытается добавить группу к фрейму
     * @param frameUID - UID фрейма
     * @param group - группа для добавления
     * @returns true если группа добавлена, false если уже захвачена другим фреймом
     */
    addGroup(frameUID: string, group: Group): boolean {
        // Если группа уже захвачена, не добавляем
        if (this.capturedUIDs.has(group.UID)) {
            return false;
        }

        // Создаем массив для фрейма, если его еще нет
        if (!this.frameToGroups.has(frameUID)) {
            this.frameToGroups.set(frameUID, []);
        }

        // Добавляем группу
        this.frameToGroups.get(frameUID)!.push(group);
        this.capturedUIDs.add(group.UID);
        return true;
    }

    /**
     * Получает все группы, захваченные конкретным фреймом
     * @param frameUID - UID фрейма
     * @returns массив групп или пустой массив
     */
    getGroups(frameUID: string): Group[] {
        return this.frameToGroups.get(frameUID) || [];
    }

    /**
     * Собирает все группы для удаления, включая overlapped группы фреймов
     * @param groups - исходные группы для удаления
     * @returns массив всех групп с учетом overlapped
     */
    collectGroupsForDeletion(groups: Group[]): Group[] {
        const result: Group[] = [];
        this.deletionUIDs.clear();

        for (const group of groups) {
            if (this.markForDeletion(group.UID)) {
                result.push(group);

                // Если это фрейм, добавляем все overlapped группы
                const child = group.onlyChild();
                if (child instanceof frameWidget) {
                    const overlappedGroups = child.getOverlappedGroups();
                    for (const overlapped of overlappedGroups) {
                        if (this.markForDeletion(overlapped.UID)) {
                            result.push(overlapped);
                        }
                    }
                }
            }
        }

        this.deletionUIDs.clear();
        return result;
    }
}

export const frameOverlappedManager = new FrameOverlappedManager();
